import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Casino as SpinIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const TRIGGER_META = {
  listing_publish: {
    label: 'After citizen listing',
    hint: 'Opens Locsy Spin when a citizen publishes a listing. Skip is recommended on.',
  },
  shop_screen: {
    label: 'Mitra shop screen',
    hint: 'Mitra offers Shop Spin to a customer phone. Global switch + Mitra opt-in both required. Coupons come from that Mitra’s templates.',
  },
  bill_create: {
    label: 'After bill create',
    hint: 'Optional bonus spin after billing. Does NOT replace auto-allot templates on bill create.',
  },
  ad_engage: {
    label: 'Digital pamphlets (ad view)',
    hint:
      'Citizen taps Viewed · Spin & Reveal. No numbers / no Better luck. Coupons come from Mitra Share-with-Locsy pool, distributed by ad category below (higher ad price → higher pool % by default).',
  },
};

const DEFAULT_COLORS = [
  '#E53935',
  '#1E88E5',
  '#43A047',
  '#FB8C00',
  '#8E24AA',
  '#00897B',
  '#C2185B',
  '#5E35B1',
  '#D81B60',
  '#00695C',
];

/** Default weights — higher numbers are rarer. Coins ₹ defaults to the number. */
const NUMBER_PRESET = [
  { n: 1, weight: 18 },
  { n: 2, weight: 16 },
  { n: 3, weight: 14 },
  { n: 4, weight: 12 },
  { n: 5, weight: 10 },
  { n: 6, weight: 8 },
  { n: 7, weight: 7 },
  { n: 8, weight: 6 },
  { n: 9, weight: 5 },
  { n: 10, weight: 4 },
];

const BETTER_LUCK_COLOR = '#78909C';
const DEFAULT_BETTER_LUCK_WEIGHT = 15;

/**
 * Quick reward-bias presets (weights + coin amounts).
 * Admin can still edit any row after applying.
 */
const REWARD_BIAS_PRESETS = {
  low: {
    key: 'low',
    label: 'Low (1–3)',
    hint: 'Most spins land on 1–3 coupons/coins',
    weights: [28, 24, 20, 8, 6, 4, 3, 2, 2, 1],
    coins: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  },
  medium: {
    key: 'medium',
    label: 'Medium (4–6)',
    hint: 'Most spins land on 4–6 coupons/coins',
    weights: [5, 6, 8, 22, 20, 18, 8, 5, 4, 3],
    coins: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  },
  high: {
    key: 'high',
    label: 'High (7–19)',
    hint: 'Bigger prizes more often; top coins ~7–19',
    weights: [2, 3, 4, 5, 6, 8, 14, 16, 18, 20],
    coins: [1, 2, 3, 4, 5, 6, 7, 10, 14, 19],
  },
  balanced: {
    key: 'balanced',
    label: 'Balanced',
    hint: 'Default mix — small wins common, big wins rarer',
    weights: [18, 16, 14, 12, 10, 8, 7, 6, 5, 4],
    coins: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  },
};

function buildNumberSegmentsFromBias(preset, keepMissSeg) {
  const ts = Date.now();
  const numbers = NUMBER_PRESET.map(({ n }, i) => ({
    id: `lws_new_${ts}_${n}`,
    label: String(n),
    prizeType: 'number',
    discountPercent: 0,
    coinAmount: preset.coins[i] ?? n,
    weight: preset.weights[i] ?? 10,
    color: DEFAULT_COLORS[i % DEFAULT_COLORS.length],
    sortOrder: i,
    enabled: true,
  }));
  if (!keepMissSeg) return numbers;
  return [
    ...numbers,
    {
      ...keepMissSeg,
      id: keepMissSeg.id || `lws_miss_${ts}`,
      prizeType: 'none',
      coinAmount: 0,
      discountPercent: 0,
      sortOrder: keepMissSeg.sortOrder ?? 99,
      enabled: keepMissSeg.enabled !== false,
    },
  ];
}

function findBetterLuckSegment(segments = []) {
  return segments.find((s) => s.prizeType === 'none') || null;
}

/**
 * Locsy Spin control — numbers 1–10 wheel.
 * Land on N → N coupons (Share-with-Locsy / Mitra) + admin Coins ₹ for that wedge.
 */
const LuckyWheel = () => {
  const [configs, setConfigs] = useState([]);
  const [stats, setStats] = useState(null);
  const [flowRules, setFlowRules] = useState([]);
  const [digidropUi, setDigidropUi] = useState({
    guaranteeCouponWhenStocked: true,
    relaxWorthToIssue: true,
    worthFallbackEnabled: true,
    globalDailyCitizenCap: 8,
  });
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wheelRes, rulesRes, uiRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/lucky-wheel/admin/configs`, {
          headers: authHeaders(),
        }),
        axios
          .get(`${API_BASE_URL}/business-posts/coupon-flow/rules`, {
            headers: authHeaders(),
          })
          .catch(() => null),
        axios
          .get(`${API_BASE_URL}/business-posts/coupon-flow/ui-settings`, {
            headers: authHeaders(),
          })
          .catch(() => null),
      ]);
      if (wheelRes.data?.success) {
        const raw = wheelRes.data.data?.configs || [];
        // Default: coins = number (1→1 coin). Fill missing/0 from label.
        const normalized = raw.map((c) => ({
          ...c,
          awardCoupons: c.awardCoupons !== false,
          awardCoins: c.awardCoins !== false,
          couponMode: c.couponMode || 'match_number',
          coinMode: c.coinMode || 'per_segment',
          shareInviteEnabled: c.shareInviteEnabled !== false,
          shareInviteCoins: Number(c.shareInviteCoins ?? 2),
          shareInviteCoupons: Number(c.shareInviteCoupons ?? 1),
          segments: (c.segments || []).map((s, i) => {
            if (s.prizeType === 'none') {
              return {
                ...s,
                label: s.label || 'Better luck',
                prizeType: 'none',
                coinAmount: 0,
                color: s.color || BETTER_LUCK_COLOR,
              };
            }
            const n = Math.min(
              10,
              Math.max(1, Number.parseInt(String(s.label || i + 1), 10) || i + 1),
            );
            const coins = Number(s.coinAmount);
            return {
              ...s,
              label: String(n),
              prizeType: 'number',
              coinAmount: Number.isFinite(coins) && coins > 0 ? coins : n,
            };
          }),
        }));
        setConfigs(normalized);
        setStats(wheelRes.data.data?.stats || null);
      }
      if (rulesRes?.data?.success) {
        setFlowRules(rulesRes.data.data?.rules || []);
      }
      if (uiRes?.data?.success && uiRes.data.data) {
        const u = uiRes.data.data;
        setDigidropUi({
          guaranteeCouponWhenStocked: u.guaranteeCouponWhenStocked !== false,
          relaxWorthToIssue: u.relaxWorthToIssue !== false,
          worthFallbackEnabled: u.worthFallbackEnabled !== false,
          globalDailyCitizenCap: Number(u.globalDailyCitizenCap ?? 8),
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load Locsy Spin');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Locsy Spin - LOCSY';
    load();
  }, [load]);

  const active = configs[tab] || null;

  const updateActive = (patch) => {
    setConfigs((prev) =>
      prev.map((c, i) => (i === tab ? { ...c, ...patch } : c)),
    );
  };

  const updateSegment = (segIndex, patch) => {
    setConfigs((prev) =>
      prev.map((c, i) => {
        if (i !== tab) return c;
        const segments = (c.segments || []).map((s, si) =>
          si === segIndex ? { ...s, ...patch } : s,
        );
        return { ...c, segments };
      }),
    );
  };

  const addSegment = () => {
    if (!active) return;
    const n = (active.segments || []).length;
    const nextNum = Math.min(10, n + 1);
    updateActive({
      segments: [
        ...(active.segments || []),
        {
          id: `lws_new_${Date.now()}`,
          label: String(nextNum),
          prizeType: 'number',
          discountPercent: 0,
          coinAmount: nextNum,
          weight: 10,
          color: DEFAULT_COLORS[n % DEFAULT_COLORS.length],
          sortOrder: n,
          enabled: true,
        },
      ],
    });
  };

  const removeSegment = (segIndex) => {
    updateActive({
      segments: (active.segments || []).filter((_, i) => i !== segIndex),
    });
  };

  const weightTotal = useMemo(() => {
    if (!active) return 0;
    return (active.segments || [])
      .filter((s) => s.enabled !== false)
      .reduce((a, s) => a + Number(s.weight || 0), 0);
  }, [active]);

  const hasBetterLuck = useMemo(() => {
    if (!active) return false;
    const miss = findBetterLuckSegment(active.segments);
    return !!(miss && miss.enabled !== false && Number(miss.weight) > 0);
  }, [active]);

  const applyNumbersPreset = () => {
    if (!active) return;
    const miss = findBetterLuckSegment(active.segments);
    updateActive({
      awardCoins: true,
      coinMode: 'per_segment',
      couponMode: active.couponMode || 'match_number',
      segments: buildNumberSegmentsFromBias(REWARD_BIAS_PRESETS.balanced, miss),
    });
    toast.info(
      'Balanced 1–10 applied — edit Coins / Weight if needed, then Save',
    );
  };

  /** Every spin lands on a number with coins (no Better-luck wedge). */
  const applyAlwaysWinRewards = () => {
    if (!active) return;
    const numbers = (active.segments || [])
      .filter((s) => s.prizeType !== 'none')
      .map((s, i) => {
        const n = Math.min(
          10,
          Math.max(1, Number.parseInt(String(s.label || i + 1), 10) || i + 1),
        );
        const coins = Number(s.coinAmount);
        return {
          ...s,
          label: String(n),
          prizeType: 'number',
          coinAmount: Number.isFinite(coins) && coins > 0 ? coins : n,
          enabled: s.enabled !== false,
        };
      });
    const segs =
      numbers.length >= 2
        ? numbers
        : buildNumberSegmentsFromBias(REWARD_BIAS_PRESETS.balanced, null);
    updateActive({
      awardCoins: true,
      coinMode: active.coinMode || 'per_segment',
      segments: segs,
    });
    toast.info('Always win rewards — Better luck removed. Review & Save.');
  };

  /** Add / keep a Better-luck wedge so some spins award nothing. */
  const applySometimesBetterLuck = () => {
    if (!active) return;
    const existing = findBetterLuckSegment(active.segments);
    const numbers = (active.segments || []).filter((s) => s.prizeType !== 'none');
    const baseNumbers =
      numbers.length >= 2
        ? numbers
        : buildNumberSegmentsFromBias(REWARD_BIAS_PRESETS.balanced, null);
    const miss = existing
      ? {
          ...existing,
          prizeType: 'none',
          label: existing.label || 'Better luck',
          coinAmount: 0,
          weight:
            Number(existing.weight) > 0
              ? Number(existing.weight)
              : DEFAULT_BETTER_LUCK_WEIGHT,
          color: existing.color || BETTER_LUCK_COLOR,
          enabled: true,
          sortOrder: existing.sortOrder ?? 99,
        }
      : {
          id: `lws_miss_${Date.now()}`,
          label: 'Better luck',
          prizeType: 'none',
          discountPercent: 0,
          coinAmount: 0,
          weight: DEFAULT_BETTER_LUCK_WEIGHT,
          color: BETTER_LUCK_COLOR,
          sortOrder: 99,
          enabled: true,
        };
    updateActive({
      segments: [...baseNumbers, miss],
    });
    toast.info(
      'Sometimes better luck — miss wedge added (edit its Weight). Save to apply.',
    );
  };

  const applyRewardBias = (presetKey) => {
    if (!active) return;
    const preset = REWARD_BIAS_PRESETS[presetKey];
    if (!preset) return;
    const miss = findBetterLuckSegment(active.segments);
    updateActive({
      awardCoins: true,
      coinMode: 'per_segment',
      couponMode: active.couponMode || 'match_number',
      segments: buildNumberSegmentsFromBias(preset, miss),
    });
    toast.info(`${preset.label} defaults applied — customize rows if needed, then Save`);
  };

  const handleSave = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const payload = {
        ...active,
        segments: (active.segments || []).map((s, i) => {
          if (s.prizeType === 'none') {
            return {
              ...s,
              label: String(s.label || 'Better luck').slice(0, 80),
              prizeType: 'none',
              discountPercent: 0,
              coinAmount: 0,
              sortOrder: s.sortOrder ?? i,
            };
          }
          const n = Math.min(
            10,
            Math.max(1, Number.parseInt(String(s.label || i + 1), 10) || i + 1),
          );
          const coinsRaw = Number(s.coinAmount ?? n);
          return {
            ...s,
            label: String(n),
            prizeType: 'number',
            discountPercent: 0,
            coinAmount: Number.isFinite(coinsRaw) && coinsRaw > 0 ? coinsRaw : n,
            sortOrder: s.sortOrder ?? i,
          };
        }),
        coinMode: active.coinMode || 'per_segment',
        couponMode: active.couponMode || 'match_number',
        awardCoupons: active.awardCoupons !== false,
        awardCoins: active.awardCoins !== false,
        shareInviteEnabled: active.shareInviteEnabled !== false,
        shareInviteCoins: Math.max(0, Number(active.shareInviteCoins ?? 2)),
        shareInviteCoupons: Math.max(0, Number(active.shareInviteCoupons ?? 1)),
      };
      const res = await axios.put(
        `${API_BASE_URL}/lucky-wheel/admin/configs/${active.triggerKey}`,
        payload,
        { headers: authHeaders() },
      );
      if (active.triggerKey === 'ad_engage') {
        await axios.put(
          `${API_BASE_URL}/business-posts/coupon-flow/rules`,
          { rules: flowRules },
          { headers: authHeaders() },
        );
        await axios.put(
          `${API_BASE_URL}/business-posts/coupon-flow/ui-settings`,
          digidropUi,
          { headers: authHeaders() },
        );
      }
      if (res.data?.success) {
        const cfg = res.data.data?.config;
        setConfigs((prev) =>
          prev.map((c) => (c.triggerKey === cfg.triggerKey ? cfg : c)),
        );
        setStats(res.data.data?.stats || stats);
        toast.success(
          active.triggerKey === 'ad_engage'
            ? 'Ad DigiDrop + Locsy Spin saved'
            : 'Locsy Spin saved',
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateFlowRule = (placementKey, patch) => {
    setFlowRules((prev) =>
      prev.map((r) =>
        r.placementKey === placementKey ? { ...r, ...patch } : r,
      ),
    );
  };

  /** Highest ad_cost → highest pool_share_percent (sums ~100). */
  const applyPriceBasedPoolShares = () => {
    const enabled = flowRules.filter((r) => r.enabled !== false);
    const totalCost = enabled.reduce(
      (a, r) => a + Math.max(0, Number(r.adCost) || 0),
      0,
    );
    if (!totalCost) {
      toast.warn('Set Ad cost on each category first');
      return;
    }
    setFlowRules((prev) =>
      prev.map((r) => {
        if (r.enabled === false) return { ...r, poolSharePercent: 0 };
        const pct = Math.round(
          ((Math.max(0, Number(r.adCost) || 0) / totalCost) * 1000) / 10,
        );
        const coupons =
          Number(r.adCost) >= 70 ? Math.max(2, Number(r.couponsPerEngage) || 2) : Number(r.couponsPerEngage) || 1;
        return {
          ...r,
          poolSharePercent: pct,
          couponsPerEngage: coupons,
        };
      }),
    );
    toast.info('Applied highest-price → highest coupon pool %. Review & Save.');
  };

  const poolShareTotal = useMemo(
    () =>
      flowRules
        .filter((r) => r.enabled !== false)
        .reduce((a, r) => a + (Number(r.poolSharePercent) || 0), 0),
    [flowRules],
  );

  const digidropPool = stats?.digidropPool;

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={800} gutterBottom>
              Locsy Spin · Lucky Wheel
            </Typography>
            <Typography color="text.secondary" maxWidth={720}>
              Listing / shop / bill: optional numbers wheel. Digital pamphlets: DigiDrop
              reveal — Mitra Share-with-Locsy coupons by ad category (no numbers, no fake
              Better luck). Admin: <code>/content/lucky-wheel</code>
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || loading || !active}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Live-safe:</strong> bill auto-allot and DigiDrop stay unchanged. Coupons are
          cloned from Mitra / admin Share-with-Locsy templates only.{' '}
          <Button
            component={RouterLink}
            to="/content/coupon-flow"
            size="small"
            sx={{ ml: 1 }}
          >
            DigiDrop pool
          </Button>
          <Button component={RouterLink} to="/mitrascreens" size="small">
            Mitra Screens
          </Button>
          <Button component={RouterLink} to="/loksyhomescreen_controls" size="small">
            Citizen Screens
          </Button>
        </Alert>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
          {[
            { label: 'Spins today', value: stats?.spinsToday },
            { label: 'Wins today', value: stats?.winsToday },
            { label: 'Shops opted in', value: stats?.shopsEnabled },
            { label: 'Spins total', value: stats?.spinsTotal },
          ].map((card) => (
            <Paper key={card.label} sx={{ p: 2, flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <SpinIcon color="primary" fontSize="small" />
                <Typography fontWeight={700}>{card.label}</Typography>
              </Stack>
              <Typography variant="h4">{card.value ?? '—'}</Typography>
            </Paper>
          ))}
        </Stack>

        {loading ? (
          <Box textAlign="center" py={6}>
            <CircularProgress />
          </Box>
        ) : !configs.length ? (
          <Alert severity="warning">
            No wheel configs found. Run migration <code>131_lucky_wheel.sql</code> on the DB.
          </Alert>
        ) : (
          <>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              sx={{ mb: 2 }}
            >
              {configs.map((c) => (
                <Tab
                  key={c.triggerKey}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{TRIGGER_META[c.triggerKey]?.label || c.triggerKey}</span>
                      <Chip
                        size="small"
                        label={c.enabled ? 'ON' : 'OFF'}
                        color={c.enabled ? 'success' : 'default'}
                      />
                    </Stack>
                  }
                />
              ))}
            </Tabs>

            {active && (
              <Paper sx={{ p: 3 }}>
                <Typography color="text.secondary" mb={2}>
                  {TRIGGER_META[active.triggerKey]?.hint}
                </Typography>

                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Title"
                      fullWidth
                      value={active.title || ''}
                      onChange={(e) => updateActive({ title: e.target.value })}
                    />
                    <TextField
                      label="Spins / user / day"
                      type="number"
                      sx={{ minWidth: 160 }}
                      value={active.spinsPerUserPerDay ?? 1}
                      onChange={(e) =>
                        updateActive({ spinsPerUserPerDay: Number(e.target.value) })
                      }
                    />
                  </Stack>
                  <TextField
                    label="Subtitle"
                    fullWidth
                    multiline
                    minRows={2}
                    value={active.subtitle || ''}
                    onChange={(e) => updateActive({ subtitle: e.target.value })}
                  />

                  <Stack direction="row" flexWrap="wrap" gap={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!active.enabled}
                          onChange={(e) => updateActive({ enabled: e.target.checked })}
                        />
                      }
                      label="Enabled"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={active.allowSkip !== false}
                          onChange={(e) => updateActive({ allowSkip: e.target.checked })}
                        />
                      }
                      label="Allow Skip"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!active.showOdds}
                          onChange={(e) => updateActive({ showOdds: e.target.checked })}
                        />
                      }
                      label="Show odds to users"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={active.useLocsyPool !== false}
                          onChange={(e) => updateActive({ useLocsyPool: e.target.checked })}
                        />
                      }
                      label="Use Share-with-Locsy pool (listing / ads)"
                    />
                  </Stack>

                  <Divider />

                  {active.triggerKey === 'ad_engage' && (
                    <>
                      <Alert severity="success">
                        <strong>Strategy (ad views ↑):</strong> every ad view reveals a real Mitra
                        coupon when DigiDrop has stock (early bird). No numbers lottery / no fake
                        “Better luck”. Premium ad categories get a higher pool % so users keep
                        tapping higher-value ads for more offers.
                      </Alert>

                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography fontWeight={700} gutterBottom>
                          Share-with-Locsy pool (live)
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <Box flex={1}>
                            <Typography variant="h4">
                              {digidropPool?.templateCount ?? '—'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Active shared templates
                              {digidropPool?.unlimitedCount
                                ? ` · ${digidropPool.unlimitedCount} unlimited`
                                : ''}
                              {digidropPool?.limitedRemaining != null
                                ? ` · ${digidropPool.limitedRemaining} limited remaining`
                                : ''}
                            </Typography>
                          </Box>
                          <Box flex={1}>
                            <Typography variant="body2" color="text.secondary">
                              Stock Mitra coupons with Share with Locsy + DigiDrop pool qty. When
                              limited stock hits 0 → early bird ends → citizen sees “try another
                              ad / check back”.
                            </Typography>
                          </Box>
                        </Stack>
                        {!!digidropPool?.sample?.length && (
                          <Stack direction="row" flexWrap="wrap" gap={0.5} mt={1.5}>
                            {digidropPool.sample.slice(0, 8).map((s) => (
                              <Chip
                                key={s.id}
                                size="small"
                                label={`${s.name || s.code || s.id}${
                                  s.worth ? ` · ₹${s.worth}` : ''
                                }${
                                  s.poolRemaining != null
                                    ? ` · left ${s.poolRemaining}`
                                    : ' · ∞'
                                }`}
                              />
                            ))}
                          </Stack>
                        )}
                      </Paper>

                      <Paper
                        variant="outlined"
                        sx={{ p: 2, bgcolor: 'rgba(255, 122, 0, 0.04)' }}
                      >
                        <Typography fontWeight={700} gutterBottom>
                          Guarantee & fallbacks
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={digidropUi.guaranteeCouponWhenStocked !== false}
                                onChange={(e) =>
                                  setDigidropUi((u) => ({
                                    ...u,
                                    guaranteeCouponWhenStocked: e.target.checked,
                                  }))
                                }
                              />
                            }
                            label="Guarantee coupon when pool has stock"
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={digidropUi.relaxWorthToIssue !== false}
                                onChange={(e) =>
                                  setDigidropUi((u) => ({
                                    ...u,
                                    relaxWorthToIssue: e.target.checked,
                                  }))
                                }
                              />
                            }
                            label="Relax min worth → issue any shared coupon"
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={digidropUi.worthFallbackEnabled !== false}
                                onChange={(e) =>
                                  setDigidropUi((u) => ({
                                    ...u,
                                    worthFallbackEnabled: e.target.checked,
                                  }))
                                }
                              />
                            }
                            label="Worth tier fallback"
                          />
                          <TextField
                            size="small"
                            type="number"
                            label="Global daily citizen cap"
                            inputProps={{ min: 0, max: 50 }}
                            value={digidropUi.globalDailyCitizenCap ?? 8}
                            onChange={(e) =>
                              setDigidropUi((u) => ({
                                ...u,
                                globalDailyCitizenCap: Math.max(
                                  0,
                                  Math.min(50, Number(e.target.value) || 0),
                                ),
                              }))
                            }
                            sx={{ width: 200 }}
                          />
                        </Stack>
                      </Paper>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                      >
                        <Typography fontWeight={700}>
                          Ad categories · pool share total{' '}
                          {Math.round(poolShareTotal * 10) / 10}%
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={applyPriceBasedPoolShares}
                        >
                          Apply highest price → highest %
                        </Button>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Distribute available Mitra shared coupons by ad category. Default: higher
                        Ad cost → higher Pool %. Coupons / engage = how many issue per view when
                        stock remains. Daily rates saved on{' '}
                        <RouterLink to="/content/ad-settings">Ad Settings</RouterLink> sync Ad
                        cost + Pool % here automatically.
                      </Typography>
                      <Stack spacing={1.5}>
                        {flowRules.map((rule) => (
                          <Paper key={rule.placementKey} variant="outlined" sx={{ p: 1.5 }}>
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              spacing={1}
                              alignItems={{ md: 'center' }}
                            >
                              <Box sx={{ minWidth: 160, flex: 1 }}>
                                <Typography fontWeight={700}>{rule.label}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {rule.placementKey}
                                </Typography>
                              </Box>
                              <TextField
                                size="small"
                                type="number"
                                label="Ad cost"
                                value={rule.adCost ?? 0}
                                onChange={(e) =>
                                  updateFlowRule(rule.placementKey, {
                                    adCost: Math.max(0, Number(e.target.value) || 0),
                                  })
                                }
                                sx={{ width: 110 }}
                              />
                              <TextField
                                size="small"
                                type="number"
                                label="Pool %"
                                inputProps={{ min: 0, max: 100 }}
                                value={rule.poolSharePercent ?? 0}
                                onChange={(e) =>
                                  updateFlowRule(rule.placementKey, {
                                    poolSharePercent: Math.max(
                                      0,
                                      Math.min(100, Number(e.target.value) || 0),
                                    ),
                                  })
                                }
                                sx={{ width: 100 }}
                              />
                              <TextField
                                size="small"
                                type="number"
                                label="Coupons / engage"
                                inputProps={{ min: 0, max: 10 }}
                                value={rule.couponsPerEngage ?? 1}
                                onChange={(e) =>
                                  updateFlowRule(rule.placementKey, {
                                    couponsPerEngage: Math.max(
                                      0,
                                      Math.min(10, Number(e.target.value) || 0),
                                    ),
                                  })
                                }
                                sx={{ width: 130 }}
                              />
                              <TextField
                                size="small"
                                type="number"
                                label="Min worth ₹"
                                value={rule.minCouponWorth ?? 0}
                                onChange={(e) =>
                                  updateFlowRule(rule.placementKey, {
                                    minCouponWorth: Math.max(
                                      0,
                                      Number(e.target.value) || 0,
                                    ),
                                  })
                                }
                                sx={{ width: 120 }}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={rule.enabled !== false}
                                    onChange={(e) =>
                                      updateFlowRule(rule.placementKey, {
                                        enabled: e.target.checked,
                                      })
                                    }
                                  />
                                }
                                label="On"
                              />
                            </Stack>
                          </Paper>
                        ))}
                        {!flowRules.length && (
                          <Alert severity="warning">
                            No DigiDrop placement rules loaded. Check Coupon Flow API / redeploy
                            tsbackend.
                          </Alert>
                        )}
                      </Stack>
                    </>
                  )}

                  {active.triggerKey !== 'ad_engage' && (
                  <>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)' }}
                  >
                    <Typography fontWeight={700} gutterBottom>
                      Quick defaults
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                      Tap a button to fill sensible defaults. You can still edit any field below,
                      then Save. Does not auto-save.
                    </Typography>

                    <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                      1. Win mode
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} mb={0.5}>
                      <Button
                        size="small"
                        variant={!hasBetterLuck ? 'contained' : 'outlined'}
                        color="success"
                        onClick={applyAlwaysWinRewards}
                      >
                        Always win rewards
                      </Button>
                      <Button
                        size="small"
                        variant={hasBetterLuck ? 'contained' : 'outlined'}
                        color="warning"
                        onClick={applySometimesBetterLuck}
                      >
                        Sometimes better luck
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                      {hasBetterLuck
                        ? 'Some spins hit “Better luck” (no coupons/coins). Adjust that wedge’s Weight below.'
                        : 'Every spin lands on 1–10 and gets coupons/coins (when allot toggles are on).'}
                    </Typography>

                    <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                      2. Reward size bias
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} mb={0.5}>
                      {Object.values(REWARD_BIAS_PRESETS).map((p) => (
                        <Button
                          key={p.key}
                          size="small"
                          variant="outlined"
                          onClick={() => applyRewardBias(p.key)}
                        >
                          {p.label}
                        </Button>
                      ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Low ≈ mostly 1–3 · Medium ≈ mostly 4–6 · High ≈ mostly 7–10 with coins up to
                      ~19 · Balanced = default mix. Keeps Better luck wedge if already on.
                    </Typography>
                  </Paper>

                  <Alert severity="info" sx={{ mb: 0 }}>
                    <strong>Reward customization</strong> — default for every number:{' '}
                    <strong>N coupons + N coins</strong> (e.g. 1 → 1 coupon + 1 coin). Edit Coins ₹
                    on each row to override.
                  </Alert>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography fontWeight={700} mb={1}>
                      What to allot on each spin
                    </Typography>
                    {(active.awardCoupons === false ||
                      active.awardCoins === false) && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        <strong>Rewards blocked:</strong>
                        {active.awardCoupons === false
                          ? ' Allot coupons is OFF — spinner will not issue coupons.'
                          : null}
                        {active.awardCoins === false
                          ? ' Allot coins is OFF — spinner will not credit coins to My Rewards.'
                          : null}{' '}
                        Turn the switch(es) ON and click <strong>Save</strong>.
                      </Alert>
                    )}
                    <Stack direction="row" flexWrap="wrap" gap={2} mb={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={active.awardCoupons !== false}
                            onChange={(e) =>
                              updateActive({ awardCoupons: e.target.checked })
                            }
                            color="success"
                          />
                        }
                        label="Allot coupons"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={active.awardCoins !== false}
                            onChange={(e) =>
                              updateActive({ awardCoins: e.target.checked })
                            }
                            color="success"
                          />
                        }
                        label="Allot coins"
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        select
                        size="small"
                        label="Coupon count mode"
                        value={active.couponMode || 'match_number'}
                        onChange={(e) => updateActive({ couponMode: e.target.value })}
                        disabled={active.awardCoupons === false}
                        sx={{ minWidth: 200 }}
                      >
                        <MenuItem value="match_number">
                          Match number (1 → 1 coupon, 4 → 4 coupons)
                        </MenuItem>
                        <MenuItem value="fixed">Fixed coupon count</MenuItem>
                      </TextField>
                      <TextField
                        size="small"
                        type="number"
                        label="Fixed coupons"
                        value={active.fixedCouponCount ?? 0}
                        onChange={(e) =>
                          updateActive({ fixedCouponCount: Number(e.target.value) })
                        }
                        disabled={
                          active.awardCoupons === false ||
                          (active.couponMode || 'match_number') !== 'fixed'
                        }
                        sx={{ width: 140 }}
                      />
                      <TextField
                        select
                        size="small"
                        label="Coin amount mode"
                        value={active.coinMode || 'per_segment'}
                        onChange={(e) => updateActive({ coinMode: e.target.value })}
                        disabled={active.awardCoins === false}
                        sx={{ minWidth: 220 }}
                      >
                        <MenuItem value="per_segment">
                          Per-number Coins ₹ (default = number, editable below)
                        </MenuItem>
                        <MenuItem value="match_number">
                          Always match spinner number
                        </MenuItem>
                        <MenuItem value="fixed">Fixed coin amount (all numbers)</MenuItem>
                      </TextField>
                      <TextField
                        size="small"
                        type="number"
                        label="Fixed coins"
                        value={active.fixedCoinAmount ?? 0}
                        onChange={(e) =>
                          updateActive({ fixedCoinAmount: Number(e.target.value) })
                        }
                        disabled={
                          active.awardCoins === false ||
                          (active.coinMode || 'per_segment') !== 'fixed'
                        }
                        sx={{ width: 140 }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                      Coupons come from Share-with-Locsy / Mitra templates. Keep DigiDrop pool stocked
                      or spins may award coins only.
                    </Typography>
                  </Paper>
                  </>
                  )}

                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(255,109,0,0.04)' }}>
                    <Typography fontWeight={700} gutterBottom>
                      Spin share invite rewards
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      On the already-spun screen, citizens can share a referral link (system share sheet:
                      WhatsApp / Telegram / Mail). When a new citizen registers with that link, the
                      sharer gets the coupons + coins below in My Rewards.
                      {active.triggerKey !== 'ad_engage' ? (
                        <>
                          {' '}
                          <strong>
                            Set amounts on the Digital pamphlets (ad view) tab — that is the source of
                            truth for crediting.
                          </strong>
                        </>
                      ) : null}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={active.shareInviteEnabled !== false}
                          onChange={(e) =>
                            updateActive({ shareInviteEnabled: e.target.checked })
                          }
                        />
                      }
                      label="Enable Share invite on already-spun screen"
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                      <TextField
                        size="small"
                        type="number"
                        label="Coupons for sharer"
                        inputProps={{ min: 0, max: 20 }}
                        value={active.shareInviteCoupons ?? 1}
                        onChange={(e) =>
                          updateActive({
                            shareInviteCoupons: Math.max(0, Number(e.target.value) || 0),
                          })
                        }
                        disabled={active.shareInviteEnabled === false}
                        sx={{ width: 180 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Coins for sharer"
                        inputProps={{ min: 0, max: 500 }}
                        value={active.shareInviteCoins ?? 2}
                        onChange={(e) =>
                          updateActive({
                            shareInviteCoins: Math.max(0, Number(e.target.value) || 0),
                          })
                        }
                        disabled={active.shareInviteEnabled === false}
                        sx={{ width: 180 }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                      Tip: Boost Control also has a separate &quot;share_register&quot; coin rule. Set one
                      path to 0 if you do not want both.
                    </Typography>
                  </Paper>

                  <Divider />
                  {active.triggerKey !== 'ad_engage' && (
                  <>
                  <Alert severity="success" sx={{ mb: 1 }}>
                    <strong>Weight</strong> = how often that number is landed on (relative odds).
                    Example: if number 1 has weight 18 and total weight is 100, chance ≈ 18%. Higher
                    weight = more likely; lower weight = rarer (good for big prizes like 9–10).
                    It does <strong>not</strong> change how many coupons/coins you give — only the
                    chance of hitting that number.
                  </Alert>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight={700}>
                      Numbers 1–10 · weight total {weightTotal}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" onClick={applyNumbersPreset}>
                        Apply 1–10 preset
                      </Button>
                      <Button startIcon={<AddIcon />} onClick={addSegment}>
                        Add number
                      </Button>
                    </Stack>
                  </Stack>

                  <Stack spacing={1.5}>
                    {(active.segments || []).map((seg, i) => {
                      const isMiss = seg.prizeType === 'none';
                      return (
                      <Paper key={seg.id || i} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={1}
                          alignItems={{ md: 'center' }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: seg.color || (isMiss ? BETTER_LUCK_COLOR : '#1565C0'),
                              flexShrink: 0,
                            }}
                          />
                          {isMiss ? (
                            <TextField
                              size="small"
                              label="Wedge"
                              value={seg.label || 'Better luck'}
                              onChange={(e) =>
                                updateSegment(i, {
                                  label: e.target.value,
                                  prizeType: 'none',
                                  coinAmount: 0,
                                })
                              }
                              sx={{ minWidth: 140 }}
                            />
                          ) : (
                            <TextField
                              size="small"
                              type="number"
                              label="Number"
                              inputProps={{ min: 1, max: 10 }}
                              value={seg.label || ''}
                              onChange={(e) => {
                                const n = Math.min(
                                  10,
                                  Math.max(1, Number(e.target.value) || 1),
                                );
                                const prevN = Number.parseInt(String(seg.label || ''), 10);
                                const prevCoins = Number(seg.coinAmount);
                                const coinsFollowNumber =
                                  !Number.isFinite(prevCoins) ||
                                  prevCoins <= 0 ||
                                  (Number.isFinite(prevN) && prevCoins === prevN);
                                updateSegment(i, {
                                  label: String(n),
                                  prizeType: 'number',
                                  coinAmount: coinsFollowNumber ? n : prevCoins,
                                });
                              }}
                              sx={{ width: 100 }}
                            />
                          )}
                          <TextField
                            size="small"
                            select
                            label="Prize"
                            value={isMiss ? 'none' : 'number'}
                            disabled
                            sx={{ minWidth: 150 }}
                          >
                            <MenuItem value="number">N coupons + coins</MenuItem>
                            <MenuItem value="none">Better luck (no prize)</MenuItem>
                          </TextField>
                          <TextField
                            size="small"
                            type="number"
                            label="Coins"
                            helperText={isMiss ? 'No prize' : 'Default = number'}
                            inputProps={{ min: 0 }}
                            value={
                              isMiss
                                ? 0
                                : Number(seg.coinAmount) > 0
                                  ? seg.coinAmount
                                  : Number.parseInt(String(seg.label || '1'), 10) || 1
                            }
                            onChange={(e) =>
                              updateSegment(i, {
                                coinAmount: Math.max(0, Number(e.target.value) || 0),
                              })
                            }
                            sx={{ width: 110 }}
                            disabled={isMiss || active.awardCoins === false}
                          />
                          <TextField
                            size="small"
                            type="number"
                            label="Weight"
                            value={seg.weight ?? 0}
                            onChange={(e) =>
                              updateSegment(i, { weight: Number(e.target.value) })
                            }
                            sx={{ width: 90 }}
                          />
                          <TextField
                            size="small"
                            label="Color"
                            value={seg.color || ''}
                            onChange={(e) => updateSegment(i, { color: e.target.value })}
                            sx={{ width: 110 }}
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={seg.enabled !== false}
                                onChange={(e) =>
                                  updateSegment(i, { enabled: e.target.checked })
                                }
                              />
                            }
                            label="On"
                          />
                          <IconButton
                            aria-label="remove"
                            onClick={() => removeSegment(i)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                        {weightTotal > 0 && seg.enabled !== false && (
                          <Typography variant="caption" color="text.secondary">
                            ~{Math.round((Number(seg.weight || 0) / weightTotal) * 1000) / 10}%
                            chance ·{' '}
                            {isMiss
                              ? 'Better luck next time (no coupons/coins)'
                              : (() => {
                                  const n =
                                    Number.parseInt(String(seg.label || ''), 10) || 1;
                                  const coupons =
                                    active.awardCoupons === false
                                      ? 0
                                      : (active.couponMode || 'match_number') === 'fixed'
                                        ? Number(active.fixedCouponCount || 0)
                                        : n;
                                  const coins =
                                    active.awardCoins === false
                                      ? 0
                                      : (active.coinMode || 'per_segment') === 'fixed'
                                        ? Number(active.fixedCoinAmount || 0)
                                        : (active.coinMode || 'per_segment') ===
                                            'match_number'
                                          ? n
                                          : Number(seg.coinAmount) > 0
                                            ? Number(seg.coinAmount)
                                            : n;
                                  return `${coupons} coupon${coupons === 1 ? '' : 's'} + ${coins} coin${coins === 1 ? '' : 's'}`;
                                })()}
                          </Typography>
                        )}
                      </Paper>
                    );
                    })}
                  </Stack>
                  </>
                  )}
                </Stack>
              </Paper>
            )}
          </>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default LuckyWheel;
